import { Injectable, OnDestroy, inject } from "@angular/core";
import { isNil } from "lodash";
import { BehaviorSubject, Subject, of } from "rxjs";
import {
    filter,
    map,
    switchMap,
    takeUntil,
    withLatestFrom
} from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { FastighetGeometry } from "~app/common/components/map/components/menu/map-fastighet/map-fastighet-models";
import { KontaktDTO } from "~app/common/models/kontakter/KontaktDTO";
import { BasicCommand } from "~app/common/services/cqs/BasicCommand";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { RuleSeverityLevel, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ILeverantorChangedEvent } from "~app/features/virkeskop/avtal/leverantorsinformation/leverantorsinformation.component";
import { LeverantorDialogService } from "~app/features/virkeskop/virkesleverantor/dialog/leverantor-dialog.service";
import { ILeverantor } from "~app/features/virkeskop/virkesleverantor/models/virkesleverantorModel";
import { ConnectVirkesleverantorCommand } from "../commands/leverantorer/ConnectVirkesleverantorCommand";
import { CreateVirkesleverantorCommand } from "../commands/leverantorer/CreateVirkesleverantorCommand";
import { DisconnectVirkesleverantorCommand } from "../commands/leverantorer/DisconnectVirkesleverantorCommand";
import { UpdateVirkeskopsavtalLeverantorCommand } from "../commands/leverantorer/UpdateVirkesleverantorCommand";
import { Avtal } from "../models/avtal-models";
import { VirkeskopKontraktstyp } from "../models/kontrakt-models";
import { KartaService } from "../services/karta.service";
import { AvtalMediatorService } from "./avtal-mediator.service";
import { AvtalService } from "./avtal.service";
import { VirkeskopValidationService } from "./validation/virkeskop-validation.service";

@Injectable()
export class LeverantorMediatorService implements OnDestroy {
    private readonly leverantorDialogService = inject(LeverantorDialogService);
    private readonly busyService = inject(BusyService);
    private readonly kartaService = inject(KartaService);
    private readonly commandManager = inject(CommandManager);
    private readonly avtalService = inject(AvtalService);
    private readonly virkeskopValidationService = inject(VirkeskopValidationService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);

    private readonly leverantorChangeSrc = new Subject<ILeverantorChangedEvent>();
    private readonly currentAvtalChange$ = this.avtalMediatorService.avtal$
        .pipe(
            filter(avtal => !isNil(avtal?.current)),
            map(avtal => avtal.current));

    private readonly leverantorerSrc = new BehaviorSubject<ILeverantor[]>([]);
    get leverantorer$() {
        return this.leverantorerSrc.asObservable();
    }
    get leverantorChange$() {
        return this.leverantorChangeSrc.asObservable();
    }

    private readonly destroy$ = new Subject<void>();

    constructor() {
        this.currentAvtalChange$.pipe(
            switchMap(avtal => {
                if (!isNil(avtal.id)) {
                    return this.avtalService.getLeverantorer(avtal.id);
                }
                return of([]);
            }),
            takeUntil(this.destroy$)
        ).subscribe(leverantorer => {
            this.leverantorerSrc.next(leverantorer);
        });

        this.leverantorChangeSrc.pipe(
            withLatestFrom(this.currentAvtalChange$),
            takeUntil(this.destroy$)
        ).subscribe(([
            levenratorChangedEventArgs,
            avtal
        ]) => {
            this.handleLeverantorChanged(avtal, levenratorChangedEventArgs);
        });

        this.leverantorerSrc.pipe(
            takeUntil(this.destroy$)
        ).subscribe(leverantorer => this.validate(leverantorer));
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onLeverantorChanged(args: ILeverantorChangedEvent) {
        this.leverantorChangeSrc.next(args);
    }

    createVirkesleverantor(avtalGuid: string, leverantor: ILeverantor): void {
        this.updateCommand(new CreateVirkesleverantorCommand(avtalGuid, leverantor));
    }

    updateVirkesleverantor(avtalGuid: string, leverantor: ILeverantor): void {
        this.updateCommand(new UpdateVirkeskopsavtalLeverantorCommand(
            avtalGuid,
            leverantor.guid,
            leverantor.andelTaljare,
            leverantor.andelNamnare,
            leverantor.kontaktperson,
            leverantor.overstyrMatbeskedsadress));
    }

    connectVirkesleverantor(avtalGuid: string, leverantorGuid: string, andelTaljare?: number, andelNamnare?: number, kontaktperson?: boolean, orgnummer?: string): void {
        this.updateCommand(new ConnectVirkesleverantorCommand(avtalGuid, leverantorGuid, andelTaljare, andelNamnare, kontaktperson, orgnummer));
    }

    disconnectVirkesleverantor(leverantorGuid: string, avtalGuid: string): void {
        this.updateCommand(new DisconnectVirkesleverantorCommand(leverantorGuid, avtalGuid));
    }

    disconnectVirkesleverantorer(leverantorer: ILeverantor[], avtalGuid: string): void {
        leverantorer?.forEach(lev => {
            this.disconnectVirkesleverantor(lev.guid, avtalGuid);
        });
    }

    fastighetSelectedForImport(fastighet: FastighetGeometry) {
        const obs = this.kartaService.getAvtalDraftByFastighetsUuid(fastighet?.geometry?.attributes?.objekt_id);
        this.busyService.addWork(obs).subscribe({
            next: (legacy) => {
                const avtal = this.avtalMediatorService.avtal;
                avtal.ansvarigVirkeskopare = {
                    userName: legacy.ansvarigVirkeskopare,
                    name: legacy.ansvarigVirkeskopareNamn
                };
                avtal.avtalsnummer = legacy.avtalsnummer;
                avtal.benamning = legacy.avtalbenamning;

                this.avtalMediatorService.updateAvtal(avtal);
                this.avtalMediatorService.mapGoto(fastighet.geometry);

                this.disconnectVirkesleverantorer(this.leverantorerSrc.value, avtal.guid);
                legacy.leverantorer.forEach(lev => this.addLeverantor(avtal.guid, lev));
                this.leverantorerSrc.next(legacy.leverantorer);

                if (ObjectUtils.isNullOrUndefined(this.avtalMediatorService.activeKontrakt$.value)) {
                    const legacyKontrakt = legacy?.kontrakt?.length > 0 ? legacy.kontrakt[0] : null;
                    const kontrakt = this.avtalMediatorService.getOrCreateDefaultKontrakt(
                        avtal, GuidUtils.newGuid(), VirkeskopKontraktstyp.Rundvirke, legacyKontrakt.kontraktbenamning);
                    kontrakt.benamning = legacyKontrakt.kontraktbenamning;
                    kontrakt.affarsform = legacyKontrakt.affarsform;
                    kontrakt.kontraktTyp = legacyKontrakt.typ;
                    kontrakt.status = legacyKontrakt.status;
                    kontrakt.fastighetsbeteckning = legacyKontrakt.fastighetsbeteckning;
                    this.avtalMediatorService.tryUpdateDefaultKontrakt(avtal);
                }
            },
            error: () => {
                //
            }
        });
    }

    tempLeverantorerChanged(leverantorer: ILeverantor[]) {
        this.leverantorerSrc.next(leverantorer);
    }

    addLeverantor(avtalGuid: string, leverantor: ILeverantor) {
        leverantor.guid = isNil(leverantor.guid) ? GuidUtils.newGuid() : leverantor.guid;
        if (isNil(leverantor.id)) {
            this.createVirkesleverantor(avtalGuid, leverantor);
        }
        this.connectAnbudsleverantor(avtalGuid, leverantor);
    }

    connectAnbudsleverantor(avtalGuid: string, lev: ILeverantor): void {
        this.connectVirkesleverantor(
            avtalGuid,
            lev.guid,
            lev.andelTaljare,
            lev.andelNamnare,
            lev.kontaktperson,
            lev.persOrgNummer);
    }

    openRedigeraLeverantorDialog(leverantor: ILeverantor) {
        const callback = (kontakt: KontaktDTO) => this.mapKontaktToLeverantor(kontakt, leverantor);
        if (isNil(leverantor.id) || leverantor.id < 1) {
            this.leverantorDialogService.openNew(leverantor, leverantor.guid, kontakt => callback(kontakt));
        } else {
            this.leverantorDialogService.open(leverantor.id, leverantor.guid, kontakt => callback(kontakt));
        }
    }

    private updateCommand(command: BasicCommand<unknown>) {
        this.commandManager.updateCommand(command);
        this.avtalMediatorService.invalidateAvtal();
    }

    private mapKontaktToLeverantor(kontakt: KontaktDTO, leverantor: ILeverantor) {
        if (!kontakt) {
            return;
        }

        leverantor.guid = kontakt.guid;
        leverantor.namn = kontakt.kontaktNamn;
        leverantor.kontaktFornamn = kontakt.kontaktNamn;
        leverantor.kontaktFornamn = kontakt.kontaktpersonFornamn;
        leverantor.kontaktEfternamn = kontakt.kontaktpersonEfternamn;
        leverantor.persOrgNummer = kontakt.kontaktOrgnummer;
        leverantor.levNummer = kontakt.kontaktLimeId;
        leverantor.telefon = kontakt.kontaktTelefon;
        leverantor.epost = kontakt.kontaktKontaktEpost;
        leverantor.adress = kontakt.kontaktAdress;
        leverantor.momskod = kontakt.momskod;
        leverantor.momsregistreringsnummer = kontakt.momsregistreringsnummer;
        leverantor.momsnummerKontrollerat = kontakt.momsnummerKontrollerat;
        leverantor.momsnummerValideringsstatus = kontakt.momsnummerValideringsstatus;
        leverantor.momsnummerValideringstid = kontakt.momsnummerValideringstid;
        leverantor.momsnummerEjAktuellt = kontakt.momsnummerEjAktuellt;
        leverantor.anskaffningsform = kontakt.anskaffningsform;

        this.leverantorChangeSrc.next({
            leverantor: leverantor,
            changeType: "update"
        });
    }

    private handleLeverantorChanged(avtal: Avtal, args: ILeverantorChangedEvent) {
        switch (args?.changeType) {
            case "add":
                this.connectVirkesleverantor(
                    avtal.guid,
                    args.leverantor.guid,
                    args.leverantor.andelTaljare,
                    args.leverantor.andelNamnare,
                    args.leverantor.kontaktperson,
                    args.leverantor.persOrgNummer);
                break;
            case "update":
                this.updateVirkesleverantor(avtal.guid, args.leverantor);
                this.virkeskopValidationService.clearMessagesByEntity("leverantorer", [args.leverantor.guid]);
                break;
            case "remove":
                this.disconnectVirkesleverantor(args.leverantor.guid, avtal.guid);
                this.virkeskopValidationService.clearMessagesByEntity("leverantorer", [args.leverantor.guid]);
                break;
        }

        if (args?.leverantor?.id) {
            // Om leverantören har skapats via UI genom knapptryck (Skapa anbudsleverantör exempelvis) så måste vi se till att ta bort create-commandet efteråt
            // Detta triggas tyvärr "onödiga" gånger då vi inte automatiskt kan koppla ihop "SaveKontaktCommand" i och med att den använder ModelActionService
            // och kör utanför avtalets commandmanager
            this.removeCreateCommandForLeverantor(args.leverantor.guid);
        }

        this.virkeskopValidationService.validateOnChange(this.commandManager);
        this.virkeskopValidationService.refreshSenasteHandelser(this.avtalMediatorService.avtal.id);
    }

    private removeCreateCommandForLeverantor(guid: string) {
        this.commandManager.remove(new CreateVirkesleverantorCommand(this.avtalMediatorService.avtal.guid, { guid: guid } as ILeverantor));
    }

    private validate(leverantorer: ILeverantor[]) {
        const isVisible = leverantorer.some(leverantor =>
            leverantor.kontaktperson &&
            !ObjectUtils.stringHasValue(leverantor.epost));

        this.virkeskopValidationService.updateClientSideValidation(isVisible, {
            messageId: "kontaktpersonEmail",
            message: "Det saknas e-postadress för kontaktpersonen",
            entityType: "leverantorer",
            severity: RuleSeverityLevel.warning,
            strategy: RuleVisibilityStrategy.alwaysShow,
            entityGuids: [],
            context: undefined
        });
    }
}
